import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { Card, Modal, Button, Form, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';

const SeccionVinculacionAkasiaPOS = ({
    idLicencia,
    licencias,
    getInformacionDetalladaLicenciaByID
}) => {
    const { token } = useToken();
    const { showErrorToast, showInfoToast } = useToast();

    const [dashboardStatus, setDashboardStatus] = useState(null);
    const [loadingDashboardStatus, setLoadingDashboardStatus] = useState(false);
    const [posCredentials, setPosCredentials] = useState(null);
    const [mostrarPassword, setMostrarPassword] = useState(false);
    const [procesandoVinculacion, setProcesandoVinculacion] = useState(false);
    const [copiadoEmail, setCopiadoEmail] = useState(false);
    const [copiadoPass, setCopiadoPass] = useState(false);

    // Estado del modal de resolución de conflicto de correo duplicado
    const [showModalCorreoDuplicado, setShowModalCorreoDuplicado] = useState(false);
    const [correoEnConflicto, setCorreoEnConflicto] = useState('');
    const [nuevoEmail, setNuevoEmail] = useState('');
    const [modoVinculacion, setModoVinculacion] = useState('CREAR');
    const [errorModal, setErrorModal] = useState(null);

    const loadUserCredentials = async () => {
        try {
            const response = await fetch(`/api/licencia/GetUserCredentials?idLicencia=${idLicencia}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const creds = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                    setPosCredentials(creds);
                }
            }
        } catch (error) {
            console.error('Error al cargar credenciales de Akasia POS:', error);
        }
    };

    const checkDashboardStatus = async () => {
        try {
            setLoadingDashboardStatus(true);
            const response = await fetch('/api/licencia/CheckDashboardLinkageStatus', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idLicenciaNube: idLicencia })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const statusArray = JSON.parse(data.detalles);
                    if (Array.isArray(statusArray) && statusArray.length > 0) {
                        const status = statusArray[0];
                        setDashboardStatus(status);
                        if (status?.bEstaVinculada || status?.bTieneLicenciaPOS) {
                            loadUserCredentials();
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error checking dashboard status:', error);
        } finally {
            setLoadingDashboardStatus(false);
        }
    };

    useEffect(() => {
        if (licencias?.idCatTipoLicencia === 3) {
            checkDashboardStatus();
        }
    }, [idLicencia, licencias?.idCatTipoLicencia]);

    /**
     * Vinculación directa estilo Dashboard:
     * No pide datos por adelantado; se ejecuta directamente con los datos de la Nube.
     * Solo si el correo ya existe en Akasia POS se despliega el modal para ingresar uno alternativo.
     */
    const ejecutarVinculacion = async (modo = 'CREAR', emailEspecifico = null) => {
        try {
            setProcesandoVinculacion(true);
            setErrorModal(null);

            const payload = {
                idLicencia: parseInt(idLicencia)
            };

            if (emailEspecifico && emailEspecifico.trim()) {
                payload.vEmail = emailEspecifico.trim();
            }

            const response = await fetch('/api/licencia/VincularLicenciaDashboard', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = "";
                    return;
                }
                throw new Error(`${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (data.iResponseType === 1) {
                showInfoToast(data.vMessage || 'Vinculación completada exitosamente');
                setShowModalCorreoDuplicado(false);
                setNuevoEmail('');
                await checkDashboardStatus();
                await loadUserCredentials();
                if (typeof getInformacionDetalladaLicenciaByID === 'function') {
                    getInformacionDetalladaLicenciaByID();
                }
            } else {
                // Comprobar si el error se debe a correo duplicado
                const isEmailRegistered =
                    data.vCode === 'ClientEmailAlreadyRegistered' ||
                    data.vCode === 'RequestEmailAlreadyRegistered' ||
                    (data.vMessage && (data.vMessage.includes('Registered') || data.vMessage.includes('registrado')));

                if (isEmailRegistered) {
                    let emailDetectado = '';
                    if (data.detalles) {
                        try {
                            const parsed = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                            emailDetectado = parsed?.vEmail || parsed?.email || '';
                        } catch (e) {
                            // Ignorar error al parsear detalles
                        }
                    }
                    if (!emailDetectado) {
                        emailDetectado = emailEspecifico || licencias?.vEmail || '';
                    }

                    setCorreoEnConflicto(emailDetectado);
                    setModoVinculacion(modo);
                    setNuevoEmail('');
                    setErrorModal(null);
                    setShowModalCorreoDuplicado(true);
                } else {
                    showErrorToast(data.vMessage || data.vCode || 'Error al ejecutar la vinculación');
                }
            }
        } catch (error) {
            console.error('Error al ejecutar vinculación:', error);
            showErrorToast(error.message || 'Error de conexión');
        } finally {
            setProcesandoVinculacion(false);
        }
    };

    if (licencias?.idCatTipoLicencia !== 3) {
        return null;
    }

    return (
        <>
            <Card className="fs--1 mt-3 mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Vinculación Akasia POS Web</h5>
                    {dashboardStatus?.idLicenciaPOS || dashboardStatus?.bTieneLicenciaPOS ? (
                        dashboardStatus?.bEstaVinculada ? (
                            <span className="badge rounded-pill badge-soft-success">
                                ✓ Vinculada y Activa
                            </span>
                        ) : (
                            <span className="badge rounded-pill badge-soft-warning">
                                Vinculada (Inactiva)
                            </span>
                        )
                    ) : (
                        <span className="badge rounded-pill badge-soft-secondary">
                            No vinculada
                        </span>
                    )}
                </Card.Header>
                <Card.Body className="border-top">
                    {loadingDashboardStatus ? (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                    ) : dashboardStatus ? (
                        <div>
                            {/* Mensaje principal de estado */}
                            {dashboardStatus.vDetalleAccion && (
                                <div className={`alert ${dashboardStatus.bEstaVinculada ? 'alert-soft-success border-success' : 'alert-soft-info border-info'} d-flex align-items-center mb-3 py-2 px-3`}>
                                    <div className={`me-2 fs-0 ${dashboardStatus.bEstaVinculada ? 'text-success' : 'text-info'}`}>
                                        {dashboardStatus.bEstaVinculada ? '✓' : 'ℹ'}
                                    </div>
                                    <div className="flex-grow-1 text-dark fw-semi-bold">
                                        {dashboardStatus.vDetalleAccion?.replace(/ al activar desde el Dashboard/gi, ' al activar')
                                            ?.replace(/Al activar desde el Dashboard/gi, 'Al activar')
                                            ?.replace(/En el Dashboard se muestra la opción de Reactivar/gi, 'Se puede reactivar')}
                                    </div>
                                </div>
                            )}

                            {/* Resumen estructurado de datos de migración y promoción */}
                            {(dashboardStatus.idLicenciaPOS || dashboardStatus.bYaCanjeoPromocion || dashboardStatus.vFechaCanje || dashboardStatus.bEsNubeActual) && (
                                <div className="bg-light border rounded-3 p-3 mb-3">
                                    <div className="d-flex flex-column gap-2">
                                        {dashboardStatus.vNombreEmpresa ? (
                                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-200">
                                                <span className="text-muted fw-semi-bold">Empresa</span>
                                                <span className="fw-semi-bold text-dark">{dashboardStatus.vNombreEmpresa}</span>
                                            </div>
                                        ) : null}

                                        {dashboardStatus.idLicenciaPOS ? (
                                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-200">
                                                <span className="text-muted fw-semi-bold">Licencia POS Web</span>
                                                <Link
                                                    to={`/licencias/detalle/${dashboardStatus.idLicenciaPOS}`}
                                                    className="badge badge-soft-primary text-decoration-none fw-semi-bold py-1 px-2"
                                                >
                                                    #{dashboardStatus.idLicenciaPOS} &rarr; Ver Detalle
                                                </Link>
                                            </div>
                                        ) : null}

                                        {(dashboardStatus.bYaCanjeoPromocion || dashboardStatus.vFechaCanje) ? (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-200">
                                                    <span className="text-muted fw-semi-bold">Promoción</span>
                                                    <span className="badge badge-soft-success">Canjeada</span>
                                                </div>

                                                {dashboardStatus.vFechaCanje && (
                                                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-200">
                                                        <span className="text-muted fw-semi-bold">Fecha de Canje</span>
                                                        <span className="fw-semi-bold text-dark">{dashboardStatus.vFechaCanje}</span>
                                                    </div>
                                                )}

                                                {dashboardStatus.vFechaFinBeneficio && (
                                                    <div className="d-flex justify-content-between align-items-center py-1">
                                                        <span className="text-muted fw-semi-bold">Vigencia</span>
                                                        <div className="d-flex align-items-center">
                                                            <span className="fw-semi-bold text-dark me-2">
                                                                {dashboardStatus.vFechaFinBeneficio}
                                                            </span>
                                                            {dashboardStatus.iDiasOtorgadosCanje ? (
                                                                <span className="badge badge-soft-info">
                                                                    {dashboardStatus.iDiasOtorgadosCanje} días
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : dashboardStatus.bEsNubeActual ? (
                                            <div className="d-flex justify-content-between align-items-center py-1">
                                                <span className="text-muted fw-semi-bold">Promoción</span>
                                                <span className="badge badge-soft-primary">
                                                    Hereda vigencia ({dashboardStatus.iDiasEstimadosPromocion || dashboardStatus.iDiasRestantesNube} días)
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            {/* Credenciales de acceso a Akasia POS */}
                            {posCredentials?.vPOSEmail && (
                                <div className="card shadow-none border bg-light mt-3 p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0 text-primary fw-bold">
                                            Credenciales Akasia POS
                                        </h6>
                                        <a
                                            href={`https://akasiapos.com/auth/sign-in?email=${encodeURIComponent(posCredentials.vPOSEmail)}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-outline-primary btn-sm py-0 px-2"
                                        >
                                            Ir a Akasia POS &rarr;
                                        </a>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted d-block">Correo Electrónico</small>
                                        <div className="input-group input-group-sm">
                                            <input type="text" className="form-control bg-white" readOnly value={posCredentials.vPOSEmail} />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(posCredentials.vPOSEmail);
                                                    setCopiadoEmail(true);
                                                    setTimeout(() => setCopiadoEmail(false), 2000);
                                                }}
                                            >
                                                {copiadoEmail ? 'Copiado!' : 'Copiar'}
                                            </button>
                                        </div>
                                    </div>
                                    {posCredentials.vPOSPassword && (
                                        <div>
                                            <small className="text-muted d-block">Contraseña</small>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    type={mostrarPassword ? "text" : "password"}
                                                    className="form-control bg-white"
                                                    readOnly
                                                    value={posCredentials.vPOSPassword}
                                                />
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    type="button"
                                                    onClick={() => setMostrarPassword(!mostrarPassword)}
                                                >
                                                    {mostrarPassword ? "Ocultar" : "Mostrar"}
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    type="button"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(posCredentials.vPOSPassword);
                                                        setCopiadoPass(true);
                                                        setTimeout(() => setCopiadoPass(false), 2000);
                                                    }}
                                                >
                                                    {copiadoPass ? 'Copiado!' : 'Copiar'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Botones de acción / Alertas según estatus */}
                            {!dashboardStatus.bEstaVinculada && (dashboardStatus.vAccionBoton === 'RENOVAR' || dashboardStatus.bYaCanjeoPromocion) ? (
                                <div className="alert alert-warning text-center mt-3 mb-0">
                                    El beneficio de promoción para Akasia POS ya fue canjeado previamente. Para reactivar la cuenta se requiere renovación comercial.
                                </div>
                            ) : dashboardStatus.vAccionBoton === 'REACTIVAR' || (dashboardStatus.bTieneLicenciaPOS && !dashboardStatus.bEstaVinculada) ? (
                                <div className="mt-3">
                                    <button
                                        className="btn btn-warning w-100 fw-semi-bold"
                                        onClick={() => ejecutarVinculacion('REACTIVAR')}
                                        disabled={procesandoVinculacion}
                                    >
                                        {procesandoVinculacion ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-2" />
                                                Reactivando Akasia POS...
                                            </>
                                        ) : (
                                            'Reactivar Akasia POS'
                                        )}
                                    </button>
                                </div>
                            ) : !dashboardStatus.bEstaVinculada && !dashboardStatus.bTieneLicenciaPOS ? (
                                dashboardStatus.bEsNubeActual ? (
                                    <div className="mt-3">
                                        <button
                                            className="btn btn-primary w-100 fw-semi-bold"
                                            onClick={() => ejecutarVinculacion('CREAR')}
                                            disabled={procesandoVinculacion}
                                        >
                                            {procesandoVinculacion ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-2" />
                                                    Vinculando Akasia POS...
                                                </>
                                            ) : (
                                                'Activar Akasia POS (Migración Nube)'
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="alert alert-info text-center mt-3 mb-0">
                                        <strong>Promoción de 3 meses:</strong> Para licencias Nube nuevas, la vinculación y aplicación de la promoción de 3 meses debe realizarse directamente desde el <strong>Punto de Venta (Akasia Desktop)</strong>.
                                    </div>
                                )
                            ) : dashboardStatus.bEstaVinculada ? (
                                <div className="mt-3 text-center py-1">
                                    <span className="small text-success fw-semi-bold">
                                        <span className="me-1">✓</span> Sincronización activa y vigente
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <p className="text-muted mb-0">No se pudo consultar el estado del Dashboard.</p>
                    )}
                </Card.Body>
            </Card>

            {/* Modal que se muestra ÚNICAMENTE si el correo de la cuenta ya está registrado en otra cuenta de Akasia POS */}
            <Modal
                show={showModalCorreoDuplicado}
                onHide={() => !procesandoVinculacion && setShowModalCorreoDuplicado(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton={!procesandoVinculacion}>
                    <Modal.Title className="text-warning d-flex align-items-center fs-0">
                        <span className="me-2">⚠️</span> Correo ya registrado
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-dark mb-2">
                        Ya existe una cuenta registrada en Akasia POS con el correo{' '}
                        <strong>{correoEnConflicto || 'asociado a esta licencia'}</strong>.
                    </p>
                    <p className="text-muted small mb-3">
                        ¿Deseas intentar con otro correo electrónico para crear la cuenta de usuario en Akasia POS?
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semi-bold">Nuevo Correo Electrónico</Form.Label>
                        <Form.Control
                            type="email"
                            value={nuevoEmail}
                            onChange={(e) => setNuevoEmail(e.target.value)}
                            placeholder="ejemplo@correo.com"
                            disabled={procesandoVinculacion}
                            autoFocus
                        />
                    </Form.Group>

                    {errorModal && (
                        <div className="alert alert-danger py-2 small mb-0">
                            {errorModal}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowModalCorreoDuplicado(false)}
                        disabled={procesandoVinculacion}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            if (!nuevoEmail || !nuevoEmail.trim() || !nuevoEmail.includes('@')) {
                                setErrorModal('Por favor ingresa un correo electrónico válido.');
                                return;
                            }
                            ejecutarVinculacion(modoVinculacion, nuevoEmail.trim());
                        }}
                        disabled={procesandoVinculacion || !nuevoEmail?.trim()}
                    >
                        {procesandoVinculacion ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Vinculando...
                            </>
                        ) : (
                            'Reintentar con este correo'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

SeccionVinculacionAkasiaPOS.propTypes = {
    idLicencia: PropTypes.string.isRequired,
    licencias: PropTypes.object.isRequired,
    getInformacionDetalladaLicenciaByID: PropTypes.func
};

export default SeccionVinculacionAkasiaPOS;
